import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Button, Dialog, applyServerErrors } from '@shripos/ui';

/**
 * Dialog + react-hook-form + server error mapping.
 * children: (form) => fields. onSubmit(values) may throw an ApiError.
 * The form is mounted only while open, so every opening starts from fresh defaultValues.
 */
export function FormDialog(props) {
  return props.open ? <FormDialogBody {...props} /> : null;
}

function FormDialogBody({
  onClose,
  title,
  description,
  schema,
  defaultValues,
  onSubmit,
  submitLabel = 'Save',
  tone,
  size,
  children,
}) {
  const [error, setError] = useState(null);
  const form = useForm({ resolver: schema ? zodResolver(schema) : undefined, defaultValues });

  const submit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      await onSubmit(values);
      onClose();
    } catch (err) {
      applyServerErrors(form.setError, err);
      setError(err.message);
    }
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={title}
      description={description}
      size={size}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={form.formState.isSubmitting}>
            Cancel
          </Button>
          <Button
            variant={tone === 'danger' ? 'danger' : 'primary'}
            onClick={submit}
            loading={form.formState.isSubmitting}
          >
            {submitLabel}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} noValidate className="space-y-4">
        {error && <Alert tone="danger">{error}</Alert>}
        {children(form)}
        <button type="submit" className="hidden" aria-hidden tabIndex={-1} />
      </form>
    </Dialog>
  );
}
